import Fuse from "fuse.js";

import { EntityWeightingType } from "@/src/components/tasks/intake-sections/matcher-utils";
import { Payer } from "@/src/models/payer";

export function matchPayersInList(payers: Payer[], query: Partial<Payer>): Payer[] {
  if (!payers.length) {
    return [];
  }

  const id = query.id?.trim();

  if (id) {
    const byId = payers.find((p) => p.id === id);

    return byId ? [byId] : [];
  }

  const shortId = query.shortId?.trim();

  if (shortId) {
    const byShort = payers.find((p) => p.shortId === shortId);

    return byShort ? [byShort] : [];
  }

  const primaryPayerId = query.primaryPayerId?.trim();

  if (primaryPayerId) {
    const byPrimaryPayerId = payers.filter((p) => p.primaryPayerId === primaryPayerId);

    return byPrimaryPayerId.length ? byPrimaryPayerId : [];
  }

  return [];
}

/**
 * Search for payers using fuzzy matching
 * Used by EntityMatcher for searching within existing payers
 */
export function searchPayer(payers: Payer[], query: string, weighting?: EntityWeightingType): Payer[] {
  const topPayers = weighting ?? [];
  const trimmedQuery = query.trim();

  if (!query || trimmedQuery === "" || !payers || payers.length === 0 || trimmedQuery.length < 2) {
    // No search query - just return first 20 with top payer weighting
    const sortedPayers = payers
      .map((payer) => {
        const topPayerIndex = topPayers.indexOf(payer.name);

        return {
          payer,
          isTopPayer: topPayerIndex !== -1,
          topPayerIndex: topPayerIndex !== -1 ? topPayerIndex : Infinity, // Use Infinity so non-top payers sort after
        };
      })
      .sort((a, b) => {
        // Top payers first, sorted by their order in topPayers array
        if (a.isTopPayer && !b.isTopPayer) return -1;
        if (!a.isTopPayer && b.isTopPayer) return 1;
        // If both are top payers, sort by their index in topPayers array
        if (a.isTopPayer && b.isTopPayer) {
          return a.topPayerIndex - b.topPayerIndex;
        }

        // If neither are top payers, sort alphabetically
        return a.payer.name.localeCompare(b.payer.name);
      });

    return sortedPayers.slice(0, 20).map((item) => item.payer);
  }

  // Fuzzy search
  const fuse = new Fuse(payers, {
    keys: [
      { name: "name", weight: 3 },
      { name: "otherName", weight: 2 },
      { name: "primaryPayerId", weight: 1 },
      { name: "shortId", weight: 1 },
    ],
    threshold: 0.4,
    ignoreLocation: false,
    includeScore: true,
    isCaseSensitive: false,
  });

  const results = fuse.search(trimmedQuery, { limit: 50 });

  // Apply weighting at the end - combine fuzzy search + top payer boost
  const alpha = 0.6; // How much original score matters
  const epsilon = 1 - alpha; // How much popularity matters

  const getTopScoreBias = (payerName: string, topPayers: string[]): number => {
    const rank = topPayers.indexOf(payerName);

    if (rank === -1) return 1.0; // Not a top payer

    // Simple inverse: lower rank = higher bias since top payers are at the top of the list
    return 1 / (rank + 1);
  };

  const adjustedResults = results.map((result) => {
    const topScoreBias = getTopScoreBias(result.item.name, topPayers);
    const adjustedScore = result.score! * alpha + epsilon * topScoreBias;

    return {
      item: result.item,
      score: adjustedScore,
    };
  });

  adjustedResults.sort((a, b) => a.score - b.score);

  // mixing the top 10 adjusted results with the top 10 results from the original payers
  const top10AdjustedResults = adjustedResults.slice(0, 10).map((result) => result.item);
  const top10AdjustedResultNames = new Set(top10AdjustedResults.map((payer) => payer.name));
  const top10OriginalResults = payers
    .map((payer) => payer)
    .filter((payer) => !top10AdjustedResultNames.has(payer.name))
    .slice(0, 10);

  const mixedResults = interleave(top10AdjustedResults, top10OriginalResults);

  return mixedResults;
}

function interleave<T>(a: T[], b: T[]): T[] {
  const result: T[] = [];
  const maxLen = Math.max(a.length, b.length);

  for (let i = 0; i < maxLen; i++) {
    if (i < a.length) result.push(a[i]);
    if (i < b.length) result.push(b[i]);
  }

  return result;
}
